// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { createTheme } from '@mui/material/styles'
import { tokens as t } from './tokens'

/**
 * D-UI-3 §3 — one MUI theme built from the app's tokens, so a kit component and a hand-rolled screen
 * look like one product during the migration. Compact density (data-dense pharmacy screens).
 * `cssVariables: true` lets a dark colour scheme be added later without touching any wrapper.
 *
 * Global CSS in index.css styles bare `button`, `input:focus-visible` and `:focus-visible`. MUI draws its
 * own focus; without the overrides below its inputs got a double ring and its buttons an extra outline.
 */
export const appTheme = createTheme({
  cssVariables: true,
  palette: {
    primary: { main: t.primary, dark: t.primaryHover, light: t.primarySoft, contrastText: '#ffffff' },
    secondary: { main: t.accent },
    success: { main: t.success },
    warning: { main: t.warning },
    error: { main: t.danger },
    info: { main: t.info },
    background: { default: t.bg, paper: t.surface },
    text: { primary: t.text, secondary: t.textSoft, disabled: t.textMute },
    divider: t.border,
  },
  shape: { borderRadius: t.radiusMd },
  typography: {
    fontFamily: t.fontSans,
    htmlFontSize: t.htmlFontSize,   // html is 15px (index.css); MUI's rem maths assumes 16 otherwise
    fontSize: 13,
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiButtonBase: { defaultProps: { disableRipple: true } },
    MuiButton: {
      defaultProps: { size: 'small', disableElevation: true },
      styleOverrides: {
        // index.css: `button { background: transparent }` is unlayered and would win over a filled button.
        containedPrimary: { backgroundColor: t.primary, '&:hover': { backgroundColor: t.primaryHover } },
        root: { '&:focus-visible': { outline: `2px solid ${t.primary}`, outlineOffset: 2 } },
      },
    },
    MuiInputBase: {
      defaultProps: { size: 'small' },
      // The global `input:focus-visible` outline doubles MUI's focused border.
      styleOverrides: { input: { '&:focus-visible': { outline: 'none' } } },
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: t.radiusLg } } },
    MuiChip: { defaultProps: { size: 'small' }, styleOverrides: { root: { fontWeight: 600 } } },
    MuiTooltip: { defaultProps: { arrow: true } },
    // Ripples are off, so a focused checkbox / radio / switch needs its own visible ring: the global
    // :focus-visible outline lands on MUI's invisible native input.
    MuiCheckbox: { styleOverrides: { root: { '&.Mui-focusVisible': { outline: `2px solid ${t.primary}`, outlineOffset: 1 } } } },
    MuiRadio: { styleOverrides: { root: { '&.Mui-focusVisible': { outline: `2px solid ${t.primary}`, outlineOffset: 1 } } } },
    MuiSwitch: { styleOverrides: { switchBase: { '&.Mui-focusVisible': { outline: `2px solid ${t.primary}`, outlineOffset: 1 } } } },
  },
})
